// Lot boundaries and report ranges are the client's midnights, so they are shown in the client's
// zone. In the browser's zone an allowance ending at midnight in New York reads as the day before
// anywhere west of it.

const MEDIUM_DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

// A boundary that can fall mid-day, such as the gap a timezone change opens, needs the time and the zone.
const MEDIUM_DATE_TIME: Intl.DateTimeFormatOptions = {
	...MEDIUM_DATE,
	hour: 'numeric',
	minute: '2-digit',
	timeZoneName: 'short',
};

// en-CA formats this as yyyy-MM-dd, the value a date input takes.
const ISO_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

// "Oct 31, 2026" in the client's zone.
export function clientZoneDate(utc: string | null | undefined, timeZoneId: string | null | undefined): string {
	return utc ? format(utc, timeZoneId, 'en-US', MEDIUM_DATE) : '';
}

// "Oct 31, 2026, 12:00 AM EDT" in the client's zone.
export function clientZoneDateTime(utc: string | null | undefined, timeZoneId: string | null | undefined): string {
	return utc ? format(utc, timeZoneId, 'en-US', MEDIUM_DATE_TIME) : '';
}

// "2026-10-31" in the client's zone, for a date input.
export function clientZoneIsoDate(utc: string | null | undefined, timeZoneId: string | null | undefined): string {
	return utc ? format(utc, timeZoneId, 'en-CA', ISO_DATE) : '';
}

// "Oct 31, 2026" for a picked yyyy-MM-dd. A calendar date has no instant, so it is named from its
// parts: read as a Date it is midnight in the browser's zone and can print the day before.
export function calendarDate(isoDate: string | null | undefined): string {
	const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate ?? '');
	return parts ? format(`${parts[1]}-${parts[2]}-${parts[3]}T00:00:00Z`, 'UTC', 'en-US', MEDIUM_DATE) : '';
}

// The instant a yyyy-MM-dd begins in the client's zone, the way the server places a picked date.
// The zone's offset is read twice because it can differ between UTC midnight and local midnight.
export function clientMidnightUtc(isoDate: string, timeZoneId: string | null | undefined): Date {
	const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
	if (!parts) {
		return new Date(NaN);
	}
	const wall = Date.UTC(+parts[1], +parts[2] - 1, +parts[3]);
	const first = wall - offsetMs(wall, timeZoneId);
	return new Date(wall - offsetMs(first, timeZoneId));
}

// How far the zone's wall clock is ahead of UTC at an instant; an unknown zone reads as UTC.
function offsetMs(instant: number, timeZoneId: string | null | undefined): number {
	let wall: Intl.DateTimeFormatPart[];
	try {
		wall = new Intl.DateTimeFormat('en-US', {
			timeZone: timeZoneId || 'UTC',
			hourCycle: 'h23',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		}).formatToParts(new Date(instant));
	} catch {
		return 0;
	}
	const part = (type: Intl.DateTimeFormatPartTypes): number => +(wall.find((p) => p.type === type)?.value ?? 0);
	const asUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
	return asUtc - Math.floor(instant / 1000) * 1000;
}

function format(
	utc: string,
	timeZoneId: string | null | undefined,
	locale: string,
	options: Intl.DateTimeFormatOptions
): string {
	const instant = new Date(utc);
	if (isNaN(instant.getTime())) {
		return '';
	}
	try {
		return new Intl.DateTimeFormat(locale, { ...options, timeZone: timeZoneId || 'UTC' }).format(instant);
	} catch {
		// An unknown zone id throws a RangeError; UTC still names a date rather than breaking the row.
		return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(instant);
	}
}
